// @ts-nocheck -- legacy typings pending cleanup; runtime behavior unchanged
import {
  type ImportConfidence,
  type ImportedEventDraft,
  type ImportPreview,
  type ImportPreviewSummary,
  importWarnings,
} from "@/lib/importers/contracts";

const KNOWN_UNITS = new Set([
  "mg", "g", "mcg", "ug", "µg", "ml", "l", "ui", "iu", "%",
  "gota", "gotas", "comprimido", "comprimidos", "capsula", "capsulas", "cápsula", "cápsulas",
]);

function canonicalKey(value: string | undefined) {
  if (!value) return undefined;
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
    .replace(/[^a-z0-9]+/g, " ").trim().replace(/\s+/g, "-");
}

function normalizeUnit(value: string | undefined) {
  if (!value) return undefined;
  const lower = value.toLowerCase();
  if (lower === "μg" || lower === "ug") return "µg";
  if (lower === "iu" || lower === "ui") return "UI";
  return lower;
}

export function parseDoseText(doseText: unknown, rawName?: string) {
  const text = String(doseText ?? "").trim();
  if (!text) return { warnings: [importWarnings.doseUnparsed] };
  const amountMatch = text.match(/^\s*(\d+(?:[.,]\d+)?)/);
  if (!amountMatch) return { warnings: [importWarnings.doseUnparsed] };
  const amount = Number(amountMatch[1].replace(",", "."));
  if (!Number.isFinite(amount)) return { warnings: [importWarnings.doseUnparsed] };

  const remainder = text.slice(amountMatch[0].length).trim();
  const token = remainder.split(/\s+/)[0]?.replace(/[.,;:]+$/, "");
  const normalizedToken = normalizeUnit(token);
  const unit = normalizedToken && KNOWN_UNITS.has(normalizedToken.toLowerCase()) ? normalizedToken : undefined;
  const warnings: string[] = [];
  if (!unit) {
    warnings.push(importWarnings.doseUnitMissing);
    if (token && rawName && canonicalKey(token) !== canonicalKey(rawName.split(/\s+/)[0])) {
      warnings.push(`Texto residual não interpretado como unidade: “${token}”.`);
    }
  }
  return { amount, unit, warnings };
}

function stripHtml(value: unknown) {
  return String(value ?? "")
    .replace(/<br\s*\/?>/gi, "\n").replace(/<\/div>/gi, "\n").replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ").replace(/&amp;/gi, "&").replace(/&lt;/gi, "<").replace(/&gt;/gi, ">")
    .replace(/\n{3,}/g, "\n\n").trim();
}

function validDateOnly(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
}

function confidenceFor(args: { validDate: boolean; hasName: boolean; amount?: number; unit?: string; demo?: boolean }): ImportConfidence {
  if (!args.validDate || !args.hasName) return "low";
  if (args.amount != null && args.unit) return args.demo ? "medium" : "high";
  return "medium";
}

function summarize(events: ImportedEventDraft[], exactDuplicates: number): ImportPreviewSummary {
  return {
    total: events.length,
    includedByDefault: events.filter((event) => !event.excludedByDefault).length,
    excludedDemonstrations: events.filter((event) => event.excludedByDefault).length,
    exactDuplicates,
    dateOnly: events.filter((event) => event.timePrecision === "date_only").length,
    missingDoseUnit: events.filter((event) => event.warnings.includes(importWarnings.doseUnitMissing)).length,
    unresolvedNames: events.filter((event) => !event.canonicalSubstanceKey && ["dose", "substance_use"].includes(event.eventType)).length,
    warnings: events.reduce((sum, event) => sum + event.warnings.length, 0),
  };
}

export function parseDrugsProJson(content: string, sourceFileName: string): ImportPreview {
  let root: any;
  try { root = JSON.parse(content); } catch { throw new Error("O arquivo não contém JSON válido."); }
  if (!root || typeof root !== "object" || !Array.isArray(root.u)) {
    throw new Error("Formato DrugsPro não reconhecido: lista “u” ausente.");
  }

  const events: ImportedEventDraft[] = [];
  const seen = new Set<string>();
  let exactDuplicates = 0;

  root.u.forEach((item: any, index: number) => {
    const recordId = String(item?.id ?? `row-${index}`);
    if (seen.has(recordId)) { exactDuplicates += 1; return; }
    seen.add(recordId);

    const rawName = String(item?.name ?? "").trim();
    const localDate = String(item?.dateISO ?? "").trim();
    const validDate = validDateOnly(localDate);
    const demo = item?.__fromSeededDemo === true;
    const dose = parseDoseText(item?.dose, rawName);
    const warnings = [...dose.warnings, importWarnings.dateOnly];
    if (demo) warnings.push(importWarnings.demo);
    if (!rawName) warnings.push(importWarnings.nameMissing);
    if (!validDate) warnings.push(importWarnings.dateInvalid);

    events.push({
      sourceApp: "DrugsPro Journal",
      sourceFileName,
      sourceRecordId: recordId,
      sourceExperienceId: item?.__expId == null ? undefined : String(item.__expId),
      sourcePayload: item,
      sourceIndex: index,
      eventType: "substance_use",
      localDate: validDate ? localDate : undefined,
      timePrecision: validDate ? "date_only" : "unknown",
      rawName: rawName || undefined,
      canonicalSubstanceKey: canonicalKey(rawName),
      doseAmount: dose.amount,
      doseUnit: dose.unit,
      route: item?.roa ? String(item.roa).trim().toLowerCase() : undefined,
      note: item?.note ? stripHtml(item.note) : undefined,
      confidence: confidenceFor({ validDate, hasName: Boolean(rawName), amount: dose.amount, unit: dose.unit, demo }),
      warnings,
      excludedByDefault: demo,
    });
  });

  if (root.n && typeof root.n === "object" && !Array.isArray(root.n)) {
    Object.entries(root.n).forEach(([dateKey, html], noteIndex) => {
      const note = stripHtml(html);
      if (!note) return;
      const validDate = validDateOnly(dateKey);
      events.push({
        sourceApp: "DrugsPro Journal",
        sourceFileName,
        sourceRecordId: `note:${dateKey}:${noteIndex}`,
        sourcePayload: { dateISO: dateKey, html },
        sourceIndex: root.u.length + noteIndex,
        eventType: "note",
        localDate: validDate ? dateKey : undefined,
        timePrecision: validDate ? "date_only" : "unknown",
        note,
        confidence: validDate ? "medium" : "low",
        warnings: validDate ? [importWarnings.dateOnly] : [importWarnings.dateInvalid],
      });
    });
  }

  return { sourceApp: "DrugsPro Journal", sourceFileName, events, summary: summarize(events, exactDuplicates) };
}

function parseCsvRows(content: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let index = 0; index < content.length; index += 1) {
    const char = content[index];
    const next = content[index + 1];
    if (char === '"') {
      if (quoted && next === '"') { field += '"'; index += 1; } else quoted = !quoted;
      continue;
    }
    if (char === "," && !quoted) { row.push(field); field = ""; continue; }
    if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && next === "\n") index += 1;
      row.push(field);
      if (row.some((cell) => cell.trim())) rows.push(row);
      row = []; field = ""; continue;
    }
    field += char;
  }
  row.push(field);
  if (row.some((cell) => cell.trim())) rows.push(row);
  return rows;
}

function localMinute(value: string) {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})$/);
  if (!match) return null;
  const [, y, m, d, h, min] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d), Number(h), Number(min));
  if (date.getFullYear() !== Number(y) || date.getMonth() !== Number(m) - 1 || date.getDate() !== Number(d) || date.getHours() !== Number(h) || date.getMinutes() !== Number(min)) return null;
  return `${y}-${m}-${d}T${h}:${min}:00`;
}

function normalizeType(value: string) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

function parseIngestion(details: string) {
  const firstClause = details.split(";")[0].trim();
  const match = firstClause.match(/^(.+?)\s+(\d+(?:[.,]\d+)?)\s*(mg|g|mcg|µg|μg|ug|ml|mL|UI|IU)\s+([a-zA-ZÀ-ÿ-]+)\b/i);
  if (!match) return { rawName: firstClause || undefined, warnings: [importWarnings.doseUnparsed] };
  return {
    rawName: match[1].trim(),
    doseAmount: Number(match[2].replace(",", ".")),
    doseUnit: normalizeUnit(match[3]),
    route: match[4].toLowerCase(),
    warnings: [] as string[],
  };
}

export function parseClinicalEventsCsv(content: string, sourceFileName: string): ImportPreview {
  const rows = parseCsvRows(content);
  if (rows.length < 2) throw new Error("CSV vazio ou sem linhas de dados.");
  const header = rows[0].map((cell) => normalizeType(cell));
  const indexColumn = header.indexOf("index");
  const dateColumn = header.indexOf("data_hora");
  const typeColumn = header.indexOf("tipo");
  const detailsColumn = header.indexOf("detalhes");
  if (dateColumn < 0 || typeColumn < 0 || detailsColumn < 0) {
    throw new Error("Cabeçalho não reconhecido. Esperado: index,data_hora,tipo,detalhes.");
  }

  const events: ImportedEventDraft[] = [];
  const seen = new Set<string>();
  let exactDuplicates = 0;
  rows.slice(1).forEach((row, rowOffset) => {
    const details = String(row[detailsColumn] ?? "").trim();
    const type = normalizeType(String(row[typeColumn] ?? ""));
    const dateText = String(row[dateColumn] ?? "").trim();
    const occurredAt = localMinute(dateText);
    const sourceRecordId = String(row[indexColumn] ?? "").trim() || `row-${rowOffset + 1}`;
    if (seen.has(sourceRecordId)) { exactDuplicates += 1; return; }
    seen.add(sourceRecordId);
    const warnings = occurredAt ? [importWarnings.timezoneMissing] : [importWarnings.dateInvalid];
    const sourcePayload = { index: row[indexColumn], data_hora: dateText, tipo: row[typeColumn], detalhes: details };

    if (type === "ingestao") {
      const parsed = parseIngestion(details);
      warnings.push(...parsed.warnings);
      events.push({
        sourceApp: "ClinicalOS CSV", sourceFileName, sourceRecordId, sourcePayload, sourceIndex: rowOffset,
        eventType: "dose", occurredAt: occurredAt ?? undefined, localDate: occurredAt?.slice(0, 10),
        timePrecision: occurredAt ? "minute" : "unknown", rawName: parsed.rawName,
        canonicalSubstanceKey: canonicalKey(parsed.rawName), doseAmount: parsed.doseAmount,
        doseUnit: parsed.doseUnit, route: parsed.route, note: details,
        confidence: occurredAt && parsed.rawName && parsed.doseAmount != null ? "high" : "low", warnings,
      });
    } else if (type === "refeicao") {
      events.push({
        sourceApp: "ClinicalOS CSV", sourceFileName, sourceRecordId, sourcePayload, sourceIndex: rowOffset,
        eventType: "meal", occurredAt: occurredAt ?? undefined, localDate: occurredAt?.slice(0, 10),
        timePrecision: occurredAt ? "minute" : "unknown", note: details,
        confidence: occurredAt && details ? "high" : "medium", warnings,
      });
    } else {
      events.push({
        sourceApp: "ClinicalOS CSV", sourceFileName, sourceRecordId, sourcePayload, sourceIndex: rowOffset,
        eventType: "other", occurredAt: occurredAt ?? undefined, localDate: occurredAt?.slice(0, 10),
        timePrecision: occurredAt ? "minute" : "unknown", note: details,
        confidence: occurredAt ? "medium" : "low", warnings,
      });
    }
  });

  return { sourceApp: "ClinicalOS CSV", sourceFileName, events, summary: summarize(events, exactDuplicates) };
}

export function parseImportFile(content: string, sourceFileName: string): ImportPreview {
  const lower = sourceFileName.toLowerCase();
  if (lower.endsWith(".json")) return parseDrugsProJson(content, sourceFileName);
  if (lower.endsWith(".csv")) return parseClinicalEventsCsv(content, sourceFileName);
  return content.trim().startsWith("{") ? parseDrugsProJson(content, sourceFileName) : parseClinicalEventsCsv(content, sourceFileName);
}

export async function sha256Text(content: string) {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(content));
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
